import type { Maturity } from "../integrations/shared.ts";
import type { CommandKey } from "../types/commands.ts";
import type { Evidence } from "../types/evidence.ts";
import type { EmptyAccess, EmptyAccesses } from "../types/lifecycle.ts";
import type {
  ApprovalDetails,
  Permission,
  StagedPermission,
} from "../types/permission.ts";
import type { DistributedOmit, RecursiveReadonly } from "../types/util.ts";

export type Access<
  Generated extends Record<never, Output>,
  Parameters extends Record<never, Parameter>,
  Delegates extends Record<never, Delegate<any, any>>,
> = {
  /** Access delegation
   *
   * Additional systems in which access must be provisioned as a prerequisite
   */
  readonly delegation: Delegates;
  /** Long-form description of an access parameter
   *
   * Will be displayed in small text or placeholder in notifiers
   */
  readonly description: string;
  /** Generated access outputs
   *
   * These are generated during the "stage" access lifecycle phase.
   * Only add outputs here under the following circumstances:
   *
   * 1. The content should only be determined after approval.
   * 2. Or the generation would take too long for a human to wait.
   *
   * Otherwise use parameters of type generated.
   */
  readonly generated: Generated;
  /** Short human-friendly description of an access parameter
   *
   * Displayed as the access option label in notifiers
   */
  readonly label: string;
  /** Access-definition parameters
   *
   * These configure what is being accessed, and with which entitlements
   */
  readonly parameters: Parameters;
  /** Defines how requests for this access are rendered */
  readonly render: Render<Parameters>;
  /** Maturity level of this access type
   *
   * Controls the visibility of feature maturity badges in the UI. When set to "beta" or "preview",
   * a blue "Beta" tag is displayed next to the access option label in the access request modal.
   * When set to "ga" (generally available), no badge is shown.
   *
   * @see {@link Maturity} for available values: "beta" | "preview" | "ga" | "deprecated"
   */
  readonly maturity: Maturity;
};

export type RenderingOf<A extends EmptyAccesses> = {
  [K in keyof A]: {
    /** The install ID where the access is granted */
    locator: keyof A[K]["parameters"] & string;
    /** The value corresponding to the access type
     *
     * E.g. the role if access type is "role"
     */
    name: keyof A[K]["parameters"] & string;
    /** The resource that is accessed
     *
     * Leave unset if access applies to the entire installation
     */
    resource?: keyof A[K]["parameters"] & string;

    /** Markdown instructions to inform the user of any steps or info needed to use the granted access */
    instructions?: (
      request: DistributedOmit<StagedAccessRequestOf<A, K>, "requestId">
    ) => string | undefined;
  };
};

/** Use to define an access literal */
export const Access = <
  // Need `Record<string, ...>` in order to provide user type feedback
  A extends Access<
    Record<string, Output>,
    Record<string, Parameter>,
    Record<string, Delegate<any, any>>
  >,
>(
  access: A
) => access as RecursiveReadonly<A>;

type Multivalued = {
  /** If true, this parameter can have zero or more values */
  readonly multivalued?: true;
};

// -- Output (generated) parameters --

type BaseOutput = Multivalued & {
  /** If true, this output may be omitted */
  readonly optional?: true;
};
/** Represents a single record in an access's generated object */
export type StringOutput = BaseOutput & {
  readonly type: "string";
  /** limit valid option arguments to a predefined set of values */
  readonly choices?: readonly string[];
};

/** Represents a complex object in an access's generated object */
export type ObjectOutput = BaseOutput & {
  readonly type: "object";
  readonly properties?: Record<string, Output>;
};

export type Output = ObjectOutput | StringOutput;

// -- Input parameters --

type ParameterBase = {
  /** Long-form description of an access parameter
   *
   * Will be displayed in small text or placeholder in notifiers
   */
  readonly description?: string;
  /** Short human-friendly description of an access parameter
   *
   * Displayed as a heading or item label in notifiers
   */
  readonly label: string;
  /** If true, this parameter will be hidden in the cli */
  readonly hidden?: boolean;
  /** If true, this parameter can be omitted */
  readonly optional?: true;
  /** If true, interactive modals will wait for this input prior to render */
  readonly waitForInput?: boolean;
  /** limit valid option arguments to a predefined set of values */
  readonly choices?: readonly string[];
  /** A line of text that should provide additional information about the use of this parameter */
  readonly hint?: string;
  /** The commands that support this parameter. If empty this parameter is supported by all commands. */
  readonly supportedCommands?: readonly CommandKey[];
};

/** Represents a boolean parameter in an access's permission object
 *
 * Omitting multivalued because a boolean parameter can only have one value: true or falsy (undefined or false)
 */
type BooleanParameter = ParameterBase & {
  /** An optional label assigned to the toggle or checkbox */
  readonly valueLabel: string;
  readonly type: "boolean";
};

type StringParameterBase = ParameterBase & Multivalued;

type StaticParameter = StringParameterBase & {
  /** Only applies to Slack! Whether user has to explicitly pick a value or
   * we preselect one for them.
   *
   * If false or undefined the user doesn't have to explicitly select an option.
   * We preselect one for them. Clients must opt-in by setting the preselected
   * option to true.
   */
  readonly isPreselect?: boolean;
  /** How this parameter is collected
   *
   * - static: Chosen from a list known prior to entry visualization
   */
  readonly type: "static";
};

type TextInputParameter = StringParameterBase & {
  // text input parameters hand-entered by requester
  readonly type: "input";
  // if true, text input boxes should render as multiline text areas
  readonly multiline?: true;
};

type NonStaticParameter = StringParameterBase & {
  /** How this parameter is collected
   * - generated: Determined by P0's backend
   * - dynamic: Chosen from a dynamic list populated on entry
   */
  readonly type: "dynamic" | "generated";
};

type ObjectParameter = ParameterBase & {
  readonly type: "object";
  readonly properties?: Record<string, Parameter>;
  readonly multivalued?: boolean;
};

/** Represents a single parameter in an access's permission object */
export type Parameter =
  | BooleanParameter
  | NonStaticParameter
  | ObjectParameter
  | StaticParameter
  | TextInputParameter;

/** Converts a permission parameter or generated output specification into its
 * corresponding value type.
 *
 * E.g.
 * ```
 * { type: "string", multivalued: true }
 * ```
 *
 * becomes value type `string[]`.
 */
export type ParameterType<P extends Output | Parameter> = P extends Multivalued
  ? ParameterInnerType<P>[]
  : P["optional"] extends true
    ? ParameterInnerType<P> | undefined
    : ParameterInnerType<P>;

/** Converts a parameter or output into a concrete type */
type ParameterInnerType<P extends Output | Parameter> =
  P["type"] extends "boolean"
    ? boolean
    : P["type"] extends "object"
      ? CustomObject<P>
      : CustomString<P>;

type CustomObject<T> = T extends {
  properties: Record<string, Output | Parameter>;
}
  ? {
      [K in keyof T["properties"]]: ParameterType<T["properties"][K]>;
    }
  : object;

type CustomString<T> = T extends {
  choices: readonly string[];
}
  ? T["choices"][number]
  : string;

// --- Rendering ---

type Render<Parameters extends Record<string, Parameter>> = {
  /** The instance in which access is provisioned */
  readonly locator?: keyof Parameters;
  /** The parameter that holds the policy for this access */
  readonly policy?: keyof Parameters;
  /** Optional extra fields to display */
  readonly extra?: Readonly<(keyof Parameters)[]>;
};

// --- Delegation ---

export type Delegate<
  A extends Record<string, Access<any, any, any>>,
  K extends keyof A,
> = {
  readonly resource: string;
  readonly key: K;
  readonly accesses: A;
};

export const Delegate = <
  A extends Record<string, Access<any, any, any>>,
  K extends keyof A,
>(
  delegate: Delegate<A, K>
): Delegate<A, K> => delegate;

// --- Type generation ---

export type SpecToType<T> = keyof T extends never
  ? object
  : T extends Record<string, any>
    ? { readonly [K in keyof T]: ParameterType<T[K]> }
    : never;

type RequestDelegateOf<D> =
  D extends Delegate<infer A, infer K>
    ? A[K] extends Access<infer _G, infer P, infer _D>
      ? {
          access: K;
          delegation: DelegatesOf<A[K]>;
          permission: SpecToType<P>;
        }
      : never
    : never;

export type DelegatesOf<A> =
  A extends Access<infer _G, infer _P, infer D>
    ? {
        [DD in keyof D]?: RequestDelegateOf<D[DD]>;
      }
    : never;

export type RequestOf<A> =
  A extends Access<infer _G, infer P, infer _D>
    ? {
        readonly delegation: DelegatesOf<A>;
        readonly evidence?: Evidence[];
        readonly expiryTimestamp?: number;
        readonly permission: SpecToType<P>;
        readonly principal: string;
        readonly requestedTimestamp: number;
        readonly requestId: string;
        readonly requestor: string;
        readonly status: string;
      }
    : never;

type StagedRequestDelegateOf<D> =
  D extends Delegate<infer A, infer K>
    ? A[K] extends Access<infer G, infer P, infer _D>
      ? {
          access: K;
          delegation: StagedDelegatesOf<A[K]>;
          permission: SpecToType<P>;
          generated: SpecToType<G>;
        }
      : never
    : never;

type StagedDelegatesOf<A> =
  A extends Access<infer _G, infer _P, infer D>
    ? {
        [DD in keyof D]?: StagedRequestDelegateOf<D[DD]>;
      }
    : never;

export type StagedRequestOf<A> =
  A extends Access<infer G, infer _P, infer D>
    ? Omit<RequestOf<A>, "delegation"> & {
        delegation: { [DD in keyof D]?: StagedRequestDelegateOf<D[DD]> };
        generated: SpecToType<G>;
        approvalDetails?: ApprovalDetails;
      }
    : never;

export type AnyRequestOf<A> = RequestOf<A> | StagedRequestOf<A>;

export type AccessRequestOf<
  Accesses,
  K = keyof Accesses & string,
> = K extends keyof Accesses & string
  ? { access: K } & RequestOf<Accesses[K]>
  : never;

/** Type guard to convert {@link Permission} objects to individual {@link AccessRequestOf} types
 *
 * Note that this only evaluates the type and otherwise performs no schema validation.
 */
export const isAccessRequestOf = <
  Type extends string,
  Accesses extends EmptyAccesses,
>(
  type: Type,
  value: Permission
): value is AccessRequestOf<Accesses> & { type: Type } => value.type === type;

/** Type guard to convert {@link StagedPermission} objects to individual {@link StagedAccessRequestOf} types
 *
 * Note that this only evaluates the type and otherwise performs no schema validation.
 */
export const isStagedAccessRequestOf = <
  Type extends string,
  Accesses extends EmptyAccesses,
>(
  type: Type,
  value: StagedPermission
): value is StagedAccessRequestOf<Accesses> & { type: Type } =>
  value.type === type;

export type StagedAccessRequestOf<
  Accesses,
  K = keyof Accesses & string,
> = K extends keyof Accesses & string
  ? { access: K } & StagedRequestOf<Accesses[K]>
  : never;

/** Converts a single access's specification into its permission spec */
export type TypedPermissionSpec<K extends string, N extends string, A> =
  A extends Access<infer G, infer P, infer D>
    ? {
        readonly type: K;
        readonly access: N;
        readonly delegation: {
          [DD in keyof D]: StagedRequestDelegateOf<D[DD]>;
        };
        readonly generated: SpecToType<G>;
        readonly permission: SpecToType<P>;
      }
    : never;

type UserKeys<T extends Record<string, Parameter>> = {
  [K in keyof T]: T[K]["type"] extends "generated" | "object" ? never : K;
}[keyof T];

export type ArgsOf<A> =
  A extends Access<infer _G, infer P, infer _D>
    ? {
        readonly [K in UserKeys<P>]: P[K] extends Parameter
          ? ParameterType<P[K]>
          : never;
      }
    : never;

export type PermissionOf<A extends Access<any, any, any>> =
  RequestOf<A>["permission"];

type ListableParameterType = "dynamic" | "static";

/** Keys of a specific access that should be listable */
export type ListedKeysOf<Access extends EmptyAccess> = {
  [K in keyof Access["parameters"] &
    string]: Access["parameters"][K]["type"] extends ListableParameterType
    ? K
    : never;
}[keyof Access["parameters"] & string];

/** The expected keys of this accesses' resource's lister object */
export type ListerKeysOf<
  T extends string,
  R extends Record<string, EmptyAccess>,
> = {
  [K in keyof R & string]: {
    [A in keyof R[K]["parameters"] &
      string]: R[K]["parameters"][A]["type"] extends "dynamic" | "static"
      ? `${T}_${K}_${A}`
      : never;
  }[keyof R[K]["parameters"] & string];
}[keyof R & string];
